'use client';

import { logger } from '@adornis/base/logging.js';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getClassRoomByCodeQuery } from '../../../db/api/get-classroom-by-code.js';
import type { ClassRoom } from '../../../db/classroom.js';
import { router } from '../../App.js';
import OwlLogo from '../../assets/OwlTransparent.svg';
import { Button } from '../../components/ui/button.js';
import { useCurrentUser } from '../../lib/useCurrentUser.js';
import { useCurrentUserProfile } from '../../lib/useCurrentUserProfile.js';
import { stashPendingJoinCode } from '../../lib/pending-join-code.js';
import { AuthModal } from '../auth/auth-modal.js';

/**
 * Landing page for a shareable `/join?code=XXXXXX` invite link — a thin convenience wrapper
 * around the same join code a student can type in manually (see join-class-page.tsx). The code
 * is stashed in localStorage immediately so it survives a full login/signup round-trip; Home's
 * mount effect (client/app/page.tsx) consumes it once a real user is present and jumps into the
 * join flow automatically.
 */
export default function JoinLandingPage() {
  const { t } = useTranslation();
  const { user, isLoading: isUserLoading } = useCurrentUser();
  const { role, isLoading: isProfileLoading } = useCurrentUserProfile();

  const [code, setCode] = useState<string | null>(null);
  const [classroom, setClassroom] = useState<ClassRoom | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(true);
  const [authMode, setAuthMode] = useState<'login' | 'signup' | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rawCode = params.get('code')?.trim().toUpperCase() ?? '';
    setCode(rawCode || null);
    if (rawCode) stashPendingJoinCode(rawCode);

    if (!rawCode) {
      setIsLookingUp(false);
      return;
    }

    void getClassRoomByCodeQuery(rawCode)({ _id: 1, name: 1 })
      .then(results => setClassroom(results?.[0] ?? null))
      .catch(err => logger.error(err, 'Failed to look up class by code'))
      .finally(() => setIsLookingUp(false));
  }, []);

  useEffect(() => {
    if (!user || isProfileLoading) return;
    if (role === 'student' || !role) {
      // Students (or accounts with no profile yet, e.g. mid-signup) go to Home, whose mount
      // effect consumes the stashed code and navigates into the join flow.
      router.goTo('home');
    }
  }, [user, role, isProfileLoading]);

  if (isUserLoading || isLookingUp) {
    return (
      <div className="flex items-center justify-center h-screen bg-white dark:bg-background">
        <div className="text-slate-500 dark:text-slate-400 text-lg">{t('JoinLanding.loading')}</div>
      </div>
    );
  }

  if (!code) {
    return (
      <div className="flex items-center justify-center h-screen bg-white dark:bg-background p-8 text-center">
        <p className="text-slate-600 dark:text-slate-400">{t('JoinLanding.noCode')}</p>
      </div>
    );
  }

  if (user && role === 'teacher') {
    return (
      <div className="flex items-center justify-center h-screen bg-white dark:bg-background p-8 text-center">
        <p className="text-slate-600 dark:text-slate-400 max-w-md">{t('JoinLanding.teacherAccountNotice')}</p>
      </div>
    );
  }

  if (user) {
    // Guest→student redirect is handled by the effect above; this covers the brief window while
    // the role/profile query is still in flight.
    return (
      <div className="flex items-center justify-center h-screen bg-white dark:bg-background">
        <div className="text-slate-500 dark:text-slate-400 text-lg">{t('JoinLanding.loading')}</div>
      </div>
    );
  }

  if (authMode) {
    return (
      <AuthModal
        initialMode={authMode}
        defaultRole="student"
        onAuthSuccess={() => router.goTo('home')}
        onContinueAsGuest={() => setAuthMode(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-background flex items-center justify-center p-8 relative">
      <div className="hidden lg:block absolute top-8 left-8">
        <h1 className="text-2xl font-['Futura'] font-semibold text-brand-500">gradias</h1>
      </div>
      <div className="w-full max-w-md text-center space-y-6">
        <img src={OwlLogo} alt="Gradias Owl Logo" className="w-24 h-auto mx-auto opacity-90" />
        {classroom ? (
          <>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              {t('JoinLanding.joinTitle', { className: classroom.name })}
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400">{t('JoinLanding.joinSubtitle')}</p>
          </>
        ) : (
          <p className="text-sm text-destructive">{t('JoinLanding.codeNotFound')}</p>
        )}
        <div className="flex items-center justify-center gap-3">
          <Button onClick={() => setAuthMode('signup')}>{t('JoinLanding.signUpButton')}</Button>
          <Button variant="outline" onClick={() => setAuthMode('login')}>
            {t('JoinLanding.logInButton')}
          </Button>
        </div>
      </div>
    </div>
  );
}
